import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { 
  Globe, 
  ShoppingCart, 
  TrendingUp, 
  Zap, 
  Smartphone, 
  Lightbulb,
  UtensilsCrossed,
  Bus,
  ArrowRight 
} from 'lucide-react';
import { Button } from './ui/button';

const Services = () => {
  const { t, language } = useLanguage();
  const readMoreByLang = {
    'pt-BR': 'Saiba mais',
    'pt-PT': 'Saber mais',
    en: 'Learn more',
    es: 'Saber más',
    fr: 'En savoir plus',
    it: 'Scopri di più',
    hi: 'और जानें'
  };

  const services = [
    { icon: Globe, title: t.services.service1Title, description: t.services.service1Desc, to: '/desenvolvimento-web' },
    { icon: ShoppingCart, title: t.services.service2Title, description: t.services.service2Desc, to: '/ecommerce' },
    { icon: TrendingUp, title: t.services.service3Title, description: t.services.service3Desc, to: '/marketing-digital' },
    { icon: Zap, title: t.services.service4Title, description: t.services.service4Desc, to: '/automacao' },
    { icon: Smartphone, title: t.services.service5Title, description: t.services.service5Desc, to: '/aplicativos-mobile' },
    { icon: Lightbulb, title: t.services.service6Title, description: t.services.service6Desc, to: '/consultoria-ti' },
    { icon: UtensilsCrossed, title: t.services.service7Title, description: t.services.service7Desc, to: '/wsj-pedidos' },
    { icon: Bus, title: t.services.service8Title, description: t.services.service8Desc, to: '/transporte-urbano' }
  ];

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 90;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  return (
    <section id="services" className="py-32 relative overflow-hidden w-full min-w-0">
      <div className="absolute inset-0 tech-grid opacity-20"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto w-full max-w-full box-border px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
              {t.services.title}
            </h2>
            <p className="text-base sm:text-xl text-white/50 max-w-3xl mx-auto mb-6 font-light px-1 sm:px-0 leading-relaxed">
              {t.services.subtitle}
            </p>
            <div className="w-20 h-1 bg-gradient-to-r from-yellow-500 to-yellow-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {services.map((service) => {
              const Icon = service.icon;
              const readMoreRowClassName =
                'flex items-center text-yellow-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 mt-auto';
              return (
                <div
                  key={service.title}
                  className="card-premium p-6 sm:p-8 rounded-2xl group cursor-pointer flex flex-col h-full min-w-0"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shrink-0">
                    <Icon className="w-7 h-7 text-white" />
                  </div>

                  <h3
                    className={
                      Icon === Bus
                        ? 'text-xl font-bold mb-3 text-white !text-white transition-colors group-hover:!text-yellow-500 break-words min-w-0'
                        : 'text-xl font-bold text-white mb-3 group-hover:text-yellow-500 transition-colors break-words min-w-0'
                    }
                  >
                    {service.title}
                  </h3>
                  <p className="text-white/50 leading-relaxed font-light flex-1 mb-6 break-words min-w-0">
                    {service.description}
                  </p>

                  <Link
                    to={service.to}
                    className={readMoreRowClassName}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="text-sm">{readMoreByLang[language] || readMoreByLang['pt-BR']}</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <Button
              size="lg"
              onClick={scrollToContact}
              className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-slate-900 font-bold px-10 py-7 text-lg rounded-xl shadow-2xl hover:shadow-yellow-500/50 transition-all duration-300 transform hover:scale-105 glow-gold-hover"
            >
              {t.services.cta}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
